import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate, fmtMoney, triggerFileDownload, downloadWithToast, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import SendEmailDialog from "@/components/sales/SendEmailDialog";
import {
  Plus, Trash, FilePdf, ArrowsClockwise, MagnifyingGlass, PencilLine,
  ClipboardText, ArrowRight, CheckCircle, XCircle, EnvelopeSimple, Upload, Paperclip,
} from "@phosphor-icons/react";

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  confirmed: "bg-blue-50 text-blue-700",
  partial_fulfilled: "bg-amber-50 text-amber-700",
  fulfilled: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
  converted_to_boq: "bg-purple-50 text-purple-700",
};

export default function SalesOrders() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [emailFor, setEmailFor] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [{ data: r }, { data: s }] = await Promise.all([
        api.get("/sales-orders", { params: { q, status: statusFilter } }),
        api.get("/sales-orders-stats"),
      ]);
      setRows(r || []);
      setStats(s || { total: 0, by_status: {} });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/sales-orders/${id}/status`, { status });
      toast.success(`Status → ${status.replace(/_/g, " ")}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const convertToBoq = async (so) => {
    if (!window.confirm(`Convert ${so.so_no} into a BOQ project? The BOQ will be pre-filled with these line items and the client PO details.`)) return;
    try {
      const { data } = await api.post(`/sales-orders/${so.id}/convert-to-boq`);
      toast.success(`Created ${data.boq_no}. Opening BOQ hub…`);
      await load();
      navigate("/ra-billing");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id, no) => {
    if (!window.confirm(`Delete ${no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/sales-orders/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const totalPipeline = useMemo(() =>
    Object.values(stats.by_status || {}).reduce((s, x) => s + (x.amount || 0), 0),
    [stats]);

  return (
    <div className="space-y-4 p-4" data-testid="sales-orders-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold font-display text-slate-900 tracking-tight">Sales Orders</h1>
          <div className="text-[11.5px] text-slate-500">
            {stats.total} SOs · pipeline value {fmtMoney(totalPipeline)}
            <span className="ml-3 text-slate-400 italic">SO is issued after quote acceptance, against the client's PO. Once confirmed, convert to BOQ to start execution.</span>
          </div>
        </div>
        <Button variant="ghost" size="sm" className="h-8" onClick={load} data-testid="so-refresh">
          <ArrowsClockwise size={12} className="mr-1" /> Refresh
        </Button>
      </div>

      {/* Status pipeline */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="so-stats">
        {["draft", "confirmed", "partial_fulfilled", "fulfilled", "cancelled", "converted_to_boq"].map((s) => {
          const st = stats.by_status?.[s] || { count: 0, amount: 0 };
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
              className={`text-left border rounded-sm px-3 py-2 hover:border-[#EA580C]/40 transition-colors ${statusFilter === s ? "border-[#EA580C] bg-orange-50" : "border-slate-200 bg-white"}`}
              data-testid={`so-stat-${s}`}
            >
              <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">{s.replace(/_/g, " ")}</div>
              <div className="text-[15px] font-bold text-slate-900 mt-0.5">{st.count}</div>
              <div className="text-[10.5px] text-slate-500 font-mono-num">{fmtMoney(st.amount)}</div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-3 py-2">
        <MagnifyingGlass size={14} className="text-slate-400" />
        <Input placeholder="Search SO#, quote#, client, PO#…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 text-[12px] w-96" data-testid="so-search" />
        {(q || statusFilter) && (
          <Button variant="ghost" size="sm" className="h-8 text-[11.5px] text-red-600" onClick={() => { setQ(""); setStatusFilter(""); }}>
            Clear filters
          </Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="text-[11.5px]">
              <TableHeader>
                <TableRow className="bg-[#0F172A]">
                  <TableHead className="text-white/90 w-32">SO #</TableHead>
                  <TableHead className="text-white/90 w-24">Date</TableHead>
                  <TableHead className="text-white/90 w-24">Quote Ref</TableHead>
                  <TableHead className="text-white/90 w-40">Client</TableHead>
                  <TableHead className="text-white/90 w-32">Client PO #</TableHead>
                  <TableHead className="text-white/90 w-24">Delivery</TableHead>
                  <TableHead className="text-white/90 text-right w-32">Grand Total</TableHead>
                  <TableHead className="text-white/90 text-center w-24">Status</TableHead>
                  <TableHead className="text-white/90 w-64">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">
                    {busy ? "Loading…" : "No sales orders yet. Convert an accepted quotation to create one!"}
                  </TableCell></TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`so-row-${r.so_no}`}>
                    <TableCell className="font-mono font-semibold text-slate-900">
                      {r.so_no}
                      {r.client_po_attachment_id && (
                        <Paperclip size={10} className="inline-block ml-1 text-slate-400" title="PO attachment uploaded" />
                      )}
                    </TableCell>
                    <TableCell className="text-[11px]">{fmtDate(r.date)}</TableCell>
                    <TableCell className="text-[11px] font-mono text-slate-500">{r.quote_no || "—"}</TableCell>
                    <TableCell className="text-[11.5px] text-slate-800">{r.client_name || "—"}</TableCell>
                    <TableCell className="text-[11px] font-mono text-slate-700">{r.client_po_no || "—"}</TableCell>
                    <TableCell className="text-[11px]">{r.delivery_date ? fmtDate(r.delivery_date) : "—"}</TableCell>
                    <TableCell className="text-right font-mono-num font-bold">{fmtMoney(r.grand_total)}</TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[9.5px] font-semibold uppercase tracking-widest ${STATUS_COLORS[r.status] || "bg-slate-100"}`}>
                        {r.status.replace(/_/g, " ")}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" onClick={() => setEditing(r.id)} data-testid={`so-open-${r.id}`}>
                          <PencilLine size={11} className="mr-0.5" /> Open
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" title="PDF" onClick={() => downloadWithToast(`/sales-orders/${r.id}/pdf`, `${r.so_no}.pdf`).catch(() => {})}>
                          <FilePdf size={11} />
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" title="Email" onClick={() => setEmailFor(r)} data-testid={`so-email-${r.id}`}>
                          <EnvelopeSimple size={11} />
                        </Button>
                        {r.status === "draft" && (
                          <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-blue-700 border-blue-200 hover:bg-blue-50" title="Confirm order" onClick={() => setStatus(r.id, "confirmed")}>
                            <CheckCircle size={11} />
                          </Button>
                        )}
                        {(r.status === "confirmed" || r.status === "partial_fulfilled") && !r.converted_boq_id && (
                          <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-purple-700 border-purple-200 hover:bg-purple-50" title="Convert to BOQ" onClick={() => convertToBoq(r)} data-testid={`so-convert-${r.id}`}>
                            <ArrowRight size={11} className="mr-0.5" /> BOQ
                          </Button>
                        )}
                        {r.status !== "converted_to_boq" && (
                          <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(r.id, r.so_no)} data-testid={`so-delete-${r.id}`}>
                            <Trash size={11} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {editing && <SOEditorSheet soId={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {emailFor && (
        <SendEmailDialog
          endpoint={`/sales-orders/${emailFor.id}/send-email`}
          docNo={emailFor.so_no}
          docKind="Sales Order"
          defaultTo={emailFor.email || ""}
          onClose={() => setEmailFor(null)}
          onSent={() => { setEmailFor(null); load(); }}
        />
      )}
    </div>
  );
}

/** Full-width SO editor with PO capture + delivery + line items */
function SOEditorSheet({ soId, onClose, onSaved }) {
  const [so, setSo] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/sales-orders/${soId}`);
        setSo(data);
        setForm({
          date: data.date, quote_id: data.quote_id || "", quote_no: data.quote_no || "",
          client_name: data.client_name || "", site_address: data.site_address || "",
          contact_no: data.contact_no || "", attention: data.attention || "", email: data.email || "",
          subject: data.subject || "",
          client_po_no: data.client_po_no || "", client_po_date: data.client_po_date || "",
          client_po_amount: data.client_po_amount || 0,
          delivery_date: data.delivery_date || "",
          site_contact_name: data.site_contact_name || "", site_contact_no: data.site_contact_no || "",
          items: (data.items || []).map(withKey),
          global_discount_pct: data.global_discount_pct || 0, gst_pct: data.gst_pct || 18,
          notes: data.notes || "", status: data.status || "draft",
        });
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    })();
  }, [soId]);

  const totals = useMemo(() => {
    if (!form) return { subtotal: 0, total_gst: 0, grand_total: 0, global_discount_amount: 0, total_ex_gst: 0 };
    const gst_pct = parseFloat(form.gst_pct) || 18;
    const globalDisc = parseFloat(form.global_discount_pct) || 0;
    const gFactor = Math.max(0, 1 - globalDisc / 100);
    let subtotal_list = 0;
    let subtotal_after_line = 0;
    let non_exempt_after_line = 0;
    let total_gst_eff = 0;
    for (const ln of form.items) {
      const qty = parseFloat(ln.qty) || 0;
      const price = parseFloat(ln.unit_price) || 0;
      const exempt = isDiscountExempt(ln.category);
      const lineDisc = exempt ? 0 : (parseFloat(ln.discount_pct) || 0);
      const lFactor = Math.max(0, 1 - lineDisc / 100);
      const lineGstFactor = exempt ? 1 : gFactor;
      const list_amt = qty * price;
      const line_amt = list_amt * lFactor;
      const line_gst_pct = parseFloat(ln.gst_pct) || gst_pct;
      subtotal_list += list_amt;
      subtotal_after_line += line_amt;
      if (!exempt) non_exempt_after_line += line_amt;
      total_gst_eff += line_amt * lineGstFactor * (line_gst_pct / 100);
    }
    const exempt_after_line = subtotal_after_line - non_exempt_after_line;
    const total_ex_gst = non_exempt_after_line * gFactor + exempt_after_line;
    const global_discount_amount = subtotal_list - total_ex_gst;
    return { subtotal: subtotal_list, global_discount_amount, total_ex_gst, total_gst: total_gst_eff, grand_total: total_ex_gst + total_gst_eff };
  }, [form]);

  const patchItem = (i, k, v) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: v };
    setForm({ ...form, items });
  };

  const uploadPO = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("po_no", form.client_po_no || "");
    fd.append("po_date", form.client_po_date || "");
    fd.append("po_amount", form.client_po_amount || 0);
    try {
      await api.post(`/sales-orders/${soId}/upload-po`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PO attachment uploaded");
      const { data } = await api.get(`/sales-orders/${soId}`);
      setSo(data);
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { e.target.value = ""; }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/sales-orders/${soId}`, form);
      toast.success("Saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!form) return null;

  return (
    <Sheet open onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent className="w-[min(1200px,100vw)] sm:max-w-none overflow-y-auto" data-testid="so-editor-sheet">
        <SheetHeader className="mb-3">
          <SheetTitle className="font-display flex items-center gap-2 flex-wrap">
            <ClipboardText size={18} className="text-[#EA580C]" />
            {so.so_no} · Sales Order
            <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-widest ${STATUS_COLORS[so.status]}`}>
              {so.status.replace(/_/g, " ")}
            </span>
            {form.quote_no && (
              <span className="text-[11.5px] text-slate-500 font-normal">· from Quote {form.quote_no}</span>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Client Name">
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="so-client-name" />
            </Field>
            <Field label="Attention">
              <Input value={form.attention} onChange={(e) => setForm({ ...form, attention: e.target.value })} />
            </Field>
            <Field label="Site Address">
              <Input value={form.site_address} onChange={(e) => setForm({ ...form, site_address: e.target.value })} />
            </Field>
            <Field label="Email">
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Contact No.">
              <Input value={form.contact_no} onChange={(e) => setForm({ ...form, contact_no: e.target.value })} />
            </Field>
            <Field label="Subject">
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            </Field>
          </div>

          {/* Client PO capture */}
          <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[11.5px] font-semibold uppercase tracking-widest text-slate-700">Client Purchase Order</h3>
              <label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={uploadPO} className="hidden" data-testid="so-upload-po" />
                <Button asChild variant="outline" size="sm" className="h-7 text-[11px]">
                  <span><Upload size={11} className="mr-1" /> {so.client_po_attachment_id ? "Replace" : "Upload PO"}</span>
                </Button>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Field label="Client PO #">
                <Input value={form.client_po_no} onChange={(e) => setForm({ ...form, client_po_no: e.target.value })} data-testid="so-po-no" />
              </Field>
              <Field label="PO Date">
                <Input type="date" value={form.client_po_date} onChange={(e) => setForm({ ...form, client_po_date: e.target.value })} data-testid="so-po-date" />
              </Field>
              <Field label="PO Amount ₹">
                <Input type="number" value={form.client_po_amount} onChange={(e) => setForm({ ...form, client_po_amount: parseFloat(e.target.value) || 0 })} data-testid="so-po-amount" />
              </Field>
              <Field label="Delivery Date">
                <Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} data-testid="so-delivery-date" />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
              <Field label="Site Contact Name">
                <Input value={form.site_contact_name} onChange={(e) => setForm({ ...form, site_contact_name: e.target.value })} />
              </Field>
              <Field label="Site Contact Phone">
                <Input value={form.site_contact_no} onChange={(e) => setForm({ ...form, site_contact_no: e.target.value })} />
              </Field>
            </div>
            {so.client_po_attachment_id && (
              <div className="text-[10.5px] text-emerald-700 mt-2 flex items-center gap-1">
                <Paperclip size={11} /> PO attachment on file
                <a href={`${api.defaults.baseURL}/attachments/${so.client_po_attachment_id}/download`} target="_blank" rel="noreferrer" className="underline ml-1">Download</a>
              </div>
            )}
          </div>

          {/* Items table (read-only edits on qty/rate) */}
          <div>
            <h3 className="text-[12.5px] font-semibold uppercase tracking-widest text-slate-700 mb-2">Line Items ({form.items.length})</h3>
            <div className="border border-slate-200 rounded-sm overflow-x-auto">
              <Table className="text-[11px]">
                <TableHeader>
                  <TableRow className="bg-slate-100">
                    <TableHead className="w-10">#</TableHead>
                    <TableHead className="w-28">Model</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-14 text-center">Unit</TableHead>
                    <TableHead className="w-24 text-right">Unit Price</TableHead>
                    <TableHead className="w-14 text-right">Qty</TableHead>
                    <TableHead className="w-14 text-right" title="Per-line discount %. Never shown on client PDF.">Disc%</TableHead>
                    <TableHead className="w-14 text-right">GST%</TableHead>
                    <TableHead className="w-28 text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {form.items.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-6">No line items</TableCell></TableRow>
                  )}
                  {form.items.map((ln, i) => {
                    const qty = parseFloat(ln.qty) || 0;
                    const price = parseFloat(ln.unit_price) || 0;
                    const exempt = isDiscountExempt(ln.category);
                    const lineDisc = exempt ? 0 : (parseFloat(ln.discount_pct) || 0);
                    const amount = qty * price * (1 - lineDisc / 100);
                    return (
                      <TableRow key={ln._key || `line-${i}`} data-testid={`so-line-${i}`}>
                        <TableCell className="font-mono">{i + 1}</TableCell>
                        <TableCell className="font-mono font-semibold">{ln.model}</TableCell>
                        <TableCell className="text-[10.5px] text-slate-600">
                          <div>{ln.category}</div>
                          <div>
                            {ln.description}
                            {ln.star_rating && (
                              <span className="ml-1 inline-block px-1 rounded-sm text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300">★ {ln.star_rating}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">{ln.unit}</TableCell>
                        <TableCell><Input type="number" value={ln.unit_price} onChange={(e) => patchItem(i, "unit_price", parseFloat(e.target.value) || 0)} className="h-7 text-right text-[11px] px-1.5" /></TableCell>
                        <TableCell><Input type="number" value={ln.qty} onChange={(e) => patchItem(i, "qty", parseFloat(e.target.value) || 0)} className="h-7 text-right text-[11px] px-1.5" /></TableCell>
                        <TableCell><Input type="number" min={0} max={90} step={0.5} value={exempt ? 0 : (ln.discount_pct || 0)} onChange={(e) => patchItem(i, "discount_pct", parseFloat(e.target.value) || 0)} className="h-7 text-right text-[11px] px-1.5" data-testid={`so-line-disc-${i}`} disabled={exempt} title={exempt ? "Discount not applicable — Room AC / Sky-Air rates are already bottom-line" : "Per-line discount %"} /></TableCell>
                        <TableCell><Input type="number" value={ln.gst_pct} onChange={(e) => patchItem(i, "gst_pct", parseFloat(e.target.value) || 0)} className="h-7 text-right text-[11px] px-1.5" /></TableCell>
                        <TableCell className="text-right font-mono-num font-semibold" title={lineDisc > 0 ? `List: ${fmtMoney(qty * price)} · ${lineDisc}% off` : undefined}>{fmtMoney(amount)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Totals */}
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex items-end gap-3">
              <Field label="Discount % (internal)" className="w-40">
                <Input data-testid="so-discount-pct" type="number" min={0} max={90} step={0.5} value={form.global_discount_pct} onChange={(e) => setForm({ ...form, global_discount_pct: parseFloat(e.target.value) || 0 })} className="h-8 text-right" />
                <div className="text-[9.5px] text-slate-400 mt-0.5">VRV & others only — not RA/SA/PA</div>
              </Field>
              <Field label="GST %" className="w-20">
                <Input type="number" value={form.gst_pct} onChange={(e) => setForm({ ...form, gst_pct: parseFloat(e.target.value) || 18 })} className="h-8 text-right" />
              </Field>
            </div>
            <div className="border-2 border-[#0F172A] rounded-sm px-4 py-3 min-w-[300px]" data-testid="so-totals">
              <TotalRow label="Subtotal (ex-GST)" value={totals.subtotal} />
              {totals.global_discount_amount > 0 && (
                <TotalRow label={`Discount (${form.global_discount_pct}%) · internal`} value={-totals.global_discount_amount} muted />
              )}
              <TotalRow label={`GST @ ${form.gst_pct}%`} value={totals.total_gst} muted />
              <div className="border-t border-slate-300 my-1"></div>
              <TotalRow label="GRAND TOTAL" value={totals.grand_total} big />
              {totals.global_discount_amount > 0 && (
                <div className="text-[10px] text-slate-500 mt-1 italic">Discount is baked into unit rate on the client PDF — no separate line will appear.</div>
              )}
            </div>
          </div>

          <Field label="Notes">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-4 mt-4 border-t border-slate-200 sticky bottom-0 bg-white z-10">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={() => downloadWithToast(`/sales-orders/${soId}/pdf`, `${so.so_no}.pdf`).catch(() => {})} data-testid="so-download-pdf">
            <FilePdf size={13} className="mr-1" /> Download PDF
          </Button>
          <Button onClick={save} disabled={busy} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white ml-auto" data-testid="so-save">
            {busy ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// iter71 — stable client-side key for line items (see Quotations.jsx)
let _soLineKey = 0;
const nextSoLineKey = () => `sol-${Date.now().toString(36)}-${(++_soLineKey).toString(36)}`;
const withKey = (item) => ({ _key: item._key || nextSoLineKey(), ...item });

// iter70 — VRV-style discounting does not apply to Room AC / Sky-Air (SA/PA)
const isDiscountExempt = (cat) => {
  const c = (cat || "").trim();
  return c.startsWith("Room AC") || c.startsWith("Sky Air");
};

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function TotalRow({ label, value, big, muted }) {
  return (
    <div className={`flex items-center justify-between ${big ? "text-[15px] font-bold text-[#EA580C]" : "text-[12px]"} ${muted ? "text-slate-500" : "text-slate-900"} py-0.5`}>
      <span>{label}</span>
      <span className="font-mono-num">{fmtMoney(value)}</span>
    </div>
  );
}
